import React from 'react'
import { BoxProps, Flex } from '@fundamentals'
import { Task } from '@core/entities/task/Task'
import { Button } from '@common/material'
import { useModal } from '@common/GlobalModal'
import { NextTaskStepModal } from '@modules/tasks/taskDetails/modals/NextTaskStepModal'
import { RequestChangesTaskModal } from '@modules/tasks/taskDetails/modals/RequestChangesTaskModal'
import { localizedStrings } from '@core/strings'
import { useSubmitTaskStep } from '@core/react-query/features/tasks/task'
import { useToast } from '@core/toast'
import { statuses } from '@core/theme/statuses'

type TaskRequestApproveChangesActionsProps = BoxProps & {
  task: Task
  fullWidth?: boolean
}

export const TaskRequestApproveChangesActions: React.FC<
  TaskRequestApproveChangesActionsProps
> = ({ task, fullWidth = false, ...props }) => {
  const { showModal } = useModal()
  const toast = useToast()
  const submitTaskStepMutation = useSubmitTaskStep()
  if (!task) return null

  const handleOnRequestChanges = () => {
    if (!task.hasPermission('REQUEST_CHANGES')) {
      toast.error(localizedStrings.noPermissionToRequestChanges)
      return
    }

    showModal(<RequestChangesTaskModal task={task} />)
  }

  const handleOnMoveToNextStep = () => {
    if (task.hasCurrentStepQuestions()) {
      showModal(<NextTaskStepModal task={task} />)
      return
    }

    // If the step has no questions, we can just submit the step to done
    submitTaskStepMutation.mutate(
      {
        id: task.id,
        taskTemplateId: task.template.currentVersion.templateId,
        assigneeUserId: task.assignee.id,
        stepAnswers: { answers: [] },
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.taskMovedToCompletedSuccessfully)
        },
        onError: (error) => {
          toast.error(error.message)
        },
      },
    )
  }

  const handleOnAddressChanges = () => {
    if (!task.hasPermission('SUBMIT_STEP')) {
      toast.error(localizedStrings.noPermissionToSubmitStep)
      return
    }

    showModal(<NextTaskStepModal task={task} isAddressChanges={true} />)
  }

  return (
    <Flex gap={1} {...props}>
      {!task.hasRequestedChanges() && (
        <>
          <Button
            variant='contained'
            size='small'
            fullWidth={fullWidth}
            onClick={handleOnMoveToNextStep}
            data-test={`move-to-${
              task?.isNextStepDone() ? 'done' : 'next-step'
            }-button`}
          >
            {`${localizedStrings.moveTo} ${
              task?.isNextStepDone()
                ? statuses.completed.label
                : task?.nextStep?.name
            }`}
          </Button>

          <Button
            variant='outlined'
            size='small'
            fullWidth={fullWidth}
            onClick={handleOnRequestChanges}
            data-test='request-changes-button'
          >
            {localizedStrings.requestChanges}
          </Button>
        </>
      )}

      {task.hasRequestedChanges() && (
        <Button
          variant='contained'
          size='small'
          fullWidth={fullWidth}
          onClick={handleOnAddressChanges}
          data-test='address-changes-button'
        >
          {localizedStrings.addressChanges}
        </Button>
      )}
    </Flex>
  )
}
